import React, { useState } from 'react';

import Collapse from '~/components/ui/atoms/Collapse';
import { prettifyKey } from '~/Utils';

import ConflictCardBase from './ConflictCardBase';
import ConflictOptionCard, { ResultTitleWithExpandToggle } from './ConflictOptionCard';
import { CHANGE_TYPE_LABEL, RESOLUTION_KEEP, RESOLUTION_OVERRIDE } from './constants';
import DiffValue from './DiffValue';
import type { ConflictFieldRow } from './promotionsUtils';
import { conflictDiffRows, conflictRowKind, formatValue, objectLabel } from './promotionsUtils';
import type { ConflictChoice, PromotionChange, PromotionConflict } from './types';

/** Copy for the disabled "Use incoming" option on a CREATE conflict. Overriding a create isn't
 * supported — the target already holds the key, so applying it could only blind-INSERT and collide — so the
 * only valid resolution is to skip it (or re-preview to promote it as an update). */
export const CREATE_OVERRIDE_DISABLED_TOOLTIP =
  "Overriding a create isn't supported — the target already has this item. Skip it, or re-preview to promote it as an update.";

/** One conflicting object: a collapsible card whose left rail tracks its state (pink until decided, teal
 * once a resolution is picked). Collapsed, it is just the breadcrumb + type chip; expanded, it shows the
 * object's field changes as one all-or-nothing set (the drifted field as "Incoming" vs "Now on
 * target") and the two resolution option cards, each carrying its own Result. */
const ConflictCard: React.FC<{
  change: PromotionChange;
  conflict: PromotionConflict;
  choice?: ConflictChoice;
  onResolveConflict?: (changeId: string, choice: ConflictChoice) => void;
}> = ({ change, conflict, choice, onResolveConflict }) => {
  const rows = conflictDiffRows(conflict.expected_old, conflict.actual_current, change.after_value);
  // Overriding a CREATE conflict would blind-INSERT onto a key the target already holds and collide; it is
  // not a valid resolution (only skip / re-preview). Disable the option and explain why in a tooltip.
  const overrideDisabled = change.change_type === 'CREATE';
  const isResolved = choice !== undefined;
  const label = objectLabel(change);
  const [keepResultExpanded, setKeepResultExpanded] = useState(false);
  const [incomingResultExpanded, setIncomingResultExpanded] = useState(false);

  return (
    <ConflictCardBase
      title={label}
      chipLabel={CHANGE_TYPE_LABEL[change.change_type] ?? change.change_type}
      isResolved={isResolved}
      configurationType={change.configuration_type}
      summary={
        <div className="flex flex-col gap-y-8">
          {/* The backend raises a CREATE conflict whenever the key is occupied, with no equality check, so
              the target's item can equal the incoming one — then no field differs and there is nothing to
              list, but the card must still say what the conflict is. */}
          {rows.length === 0 && <span className="text-slate-800">The target already holds an identical item.</span>}
          {rows.map((row) => {
            const fieldLabel = row.key ? prettifyKey(row.key) : 'Value';
            if (conflictRowKind(row) !== 'drift') {
              // A field the promotion changes that the target did NOT touch — an ordinary compact diff.
              return (
                <div key={row.key} className="flex flex-wrap items-center gap-x-4">
                  <span className="font-bold">{fieldLabel}:</span>
                  <DiffValue value={row.current} tone="old" />
                  <span>→</span>
                  <DiffValue value={row.target} tone="new" />
                </div>
              );
            }
            // The drifted field is the actual conflict: the value the promotion would write vs the value
            // someone put on the target since the promotion was created.
            return (
              <div key={row.key} className="grid grid-cols-[auto_1fr] justify-items-start gap-x-16 gap-y-4">
                <span className="col-span-2 font-bold">{fieldLabel}</span>
                <DriftedValue row={row} isIncoming={false} />
                <DriftedValue row={row} isIncoming={true} />
              </div>
            );
          })}
        </div>
      }
      conflictView={
        <>
          <ConflictOptionCard
            name={`resolution-${change.change_id}`}
            selected={choice === RESOLUTION_KEEP}
            title="Keep current"
            subtitle="what’s on the target now"
            description="Keeps the object as it is on the target."
            resultTitle={
              <ResultTitleWithExpandToggle
                expanded={keepResultExpanded}
                onToggleExpanded={() => setKeepResultExpanded((prev) => !prev)}
              >
                Result — target unchanged
              </ResultTitleWithExpandToggle>
            }
            onSelect={() => onResolveConflict?.(change.change_id, RESOLUTION_KEEP)}
          >
            <FullResult rows={rows} expanded={keepResultExpanded} />
          </ConflictOptionCard>
          <ConflictOptionCard
            name={`resolution-${change.change_id}`}
            selected={choice === RESOLUTION_OVERRIDE}
            disabled={overrideDisabled}
            disabledTooltip={overrideDisabled ? CREATE_OVERRIDE_DISABLED_TOOLTIP : undefined}
            title="Use incoming"
            subtitle="the incoming value"
            description="Replaces the target’s value with the incoming one."
            resultTitle={
              <ResultTitleWithExpandToggle
                expanded={incomingResultExpanded}
                onToggleExpanded={() => setIncomingResultExpanded((prev) => !prev)}
              >
                Result — applies incoming changes
              </ResultTitleWithExpandToggle>
            }
            onSelect={() => onResolveConflict?.(change.change_id, RESOLUTION_OVERRIDE)}
          >
            <FullResult isIncoming rows={rows} expanded={incomingResultExpanded} />
          </ConflictOptionCard>
        </>
      }
    />
  );
};

const DriftedValue: React.FC<{ row: ConflictFieldRow; isIncoming?: boolean }> = ({ row, isIncoming = false }) => {
  return (
    <>
      <span className="text-sm text-slate-800">{isIncoming ? 'Incoming:' : 'Now on target:'}</span>
      <span className="rounded bg-slate-200 px-4 py-2 text-sm">
        {formatValue(isIncoming ? row.target : row.current)}
      </span>
    </>
  );
};

const FullResult: React.FC<{ rows: ConflictFieldRow[]; isIncoming?: boolean; expanded?: boolean }> = ({
  rows,
  isIncoming = false,
  expanded = false,
}) => {
  return (
    <Collapse in={expanded}>
      <div className="grid grid-cols-[auto_1fr] gap-x-16 gap-y-8">
        {rows.map((row) => (
          <div key={row.key} className="col-span-2 grid grid-cols-subgrid justify-items-start">
            <span className="font-bold">{row.key ? prettifyKey(row.key) : 'Value'}:</span>
            <div className="rounded bg-slate-200 px-4 py-2">{formatValue(isIncoming ? row.target : row.current)}</div>
          </div>
        ))}
      </div>
    </Collapse>
  );
};

export default ConflictCard;
